/**
 * Tabla de variables del paso 3 del editor: alias, ejemplo y dato del cliente sugerido.
 *
 * El ejemplo es lo que Meta mira para aprobar: sin ejemplo (o con uno que no parece real) la
 * rechaza. Por eso es obligatorio y se marca en rojo en la fila, no sólo en el semáforo: con cinco
 * variables, «Cada variable necesita un ejemplo» no dice cuál falta.
 *
 * El dato sugerido sólo precarga el mapeo cuando se arma una campaña; no obliga a nada.
 */

import type { CampoDisponible, CampoVariable, ReglaResultado, VariablePlantilla } from "../types";
import { ErroresDeCampo } from "./SemaforoReglas";

interface Props {
  variables: VariablePlantilla[];
  campos: CampoDisponible[];
  onChange: (variables: VariablePlantilla[]) => void;
  /** todas las reglas del validador: se usan las de `ejemplos` y `variables_declaradas` */
  reglas: ReglaResultado[];
  /** La variable de la imagen que se elige en cada campaña: su ejemplo es la imagen de arriba y no se escribe a mano. */
  numeroImagen?: number | null;
  /** Las variables de los botones que cuentan clics (`logic/clics`): llevan un código propio en cada mensaje. */
  numerosClic?: number[];
}

/** Mismo tope que el validador del backend (LIMITES.ejemplo). */
export const MAX_EJEMPLO = 1000;

/** Qué le falta al ejemplo de una fila (mismas condiciones que la regla `ejemplos`), o "" si está bien. */
export function problemaEjemplo(ejemplo: string): string {
  const e = ejemplo || "";
  if (!e.trim()) return "Falta el ejemplo.";
  if (/[\r\n]/.test(e)) return "Sin saltos de línea.";
  if (e.length > MAX_EJEMPLO) return `Hasta ${MAX_EJEMPLO} caracteres.`;
  return "";
}

export function TablaVariables({ variables, campos, onChange, reglas, numeroImagen = null, numerosClic = [] }: Props) {
  const lista = [...(variables || [])].sort((a, b) => a.numero - b.numero);
  const fallidas = (reglas || []).filter((r) => !r.ok && (r.id === "ejemplos" || r.id === "variables_declaradas"));
  const reglaEjemplosFalla = fallidas.some((r) => r.id === "ejemplos");

  const cambiar = (numero: number, parcial: Partial<VariablePlantilla>) => {
    onChange(lista.map((v) => (v.numero === numero ? { ...v, ...parcial } : v)));
  };

  if (!lista.length) {
    return <p className="text-sm text-slate-400">Esta plantilla no tiene variables.</p>;
  }

  // Un campo sugerido que ya no está en la lista (el backend lo sacó) se sigue mostrando: si se
  // perdiera del select, al guardar se borraría sin que nadie lo haya decidido.
  const opciones = (actual?: CampoVariable) => {
    const base = campos.map((c) => ({ valor: c.campo, texto: c.etiqueta }));
    if (actual && !base.some((o) => o.valor === actual)) base.push({ valor: actual, texto: actual });
    return base;
  };

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-xl border border-slate-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left">
              <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 w-16">Variable</th>
              <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">Nombre</th>
              <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">Ejemplo para Meta</th>
              <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">Dato del cliente sugerido</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lista.map((v) => {
              if (v.numero === numeroImagen) {
                return (
                  <tr key={v.numero} className="align-top" data-testid="variable-imagen">
                    <td className="px-3 py-2 font-mono text-xs text-violet-700 whitespace-nowrap pt-3.5">{`{{${v.numero}}}`}</td>
                    <td className="px-3 py-2 pt-3.5 text-sm text-slate-700">Imagen de la campaña</td>
                    <td className="px-3 py-2 pt-3.5 text-sm text-slate-500" colSpan={2}>La imagen de ejemplo de arriba. En cada campaña se elige la que sale.</td>
                  </tr>
                );
              }
              if (numerosClic.includes(v.numero)) {
                return (
                  <tr key={v.numero} className="align-top" data-testid="variable-clic">
                    <td className="px-3 py-2 font-mono text-xs text-violet-700 whitespace-nowrap pt-3.5">{`{{${v.numero}}}`}</td>
                    <td className="px-3 py-2 pt-3.5 text-sm text-slate-700">{v.alias}</td>
                    <td className="px-3 py-2 pt-3.5 text-sm text-slate-500" colSpan={2}>Se completa sola: cada mensaje lleva su código para contar quién toca el botón.</td>
                  </tr>
                );
              }
              const problema = problemaEjemplo(v.ejemplo);
              // En rojo si la fila tiene el problema, aunque el backend todavía no haya respondido.
              const enRojo = !!problema && (reglaEjemplosFalla || !v.ejemplo.trim());
              return (
                <tr key={v.numero} className="align-top">
                  <td className="px-3 py-2 font-mono text-xs text-violet-700 whitespace-nowrap pt-3.5">{`{{${v.numero}}}`}</td>
                  <td className="px-3 py-2">
                    <input
                      aria-label={`Nombre de la variable ${v.numero}`}
                      value={v.alias}
                      onChange={(e) => cambiar(v.numero, { alias: e.target.value.slice(0, 60) })}
                      className="w-full min-w-[120px] rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      aria-label={`Ejemplo de la variable ${v.numero}`}
                      aria-invalid={enRojo || undefined}
                      value={v.ejemplo}
                      // Un salto de línea pegado se vuelve espacio: Meta no acepta ejemplos multilínea.
                      onChange={(e) => cambiar(v.numero, { ejemplo: e.target.value.replace(/[\r\n]+/g, " ") })}
                      placeholder="Ej.: Sofía"
                      className={`w-full min-w-[140px] rounded-lg border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${enRojo ? "border-red-300 bg-red-50/40" : "border-slate-200"}`}
                    />
                    {enRojo && <p className="text-[11px] text-red-600 mt-1">{problema}</p>}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      aria-label={`Dato del cliente para la variable ${v.numero}`}
                      value={v.campoSugerido || ""}
                      onChange={(e) => cambiar(v.numero, { campoSugerido: (e.target.value || undefined) as CampoVariable | undefined })}
                      className="w-full min-w-[140px] rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="">Ninguno</option>
                      {opciones(v.campoSugerido).map((o) => <option key={o.valor} value={o.valor}>{o.texto}</option>)}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ErroresDeCampo reglas={fallidas} />
    </div>
  );
}

export default TablaVariables;
